import { DrawerCloseButton, PostcodeSelect } from '@components';
import { L } from '@lib';
import { Customer } from '@services';
import { useStore } from '@stores';
import { Rules } from '@utils';
import { Button, Col, Drawer, Form, Input, Row, Space } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';

interface IProps {
  open: boolean;
  customer: Customer;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CustomerEditDrawer: React.FC<IProps> = observer(
  ({ open, customer, onClose, onSuccess }) => {
    const { customerStore } = useStore();
    const [form] = Form.useForm();

    useEffect(() => {
      if (open && customer) {
        form.setFieldsValue(customer);
      }
    }, [open, customer, form]);

    const handleFinish = useCallback(async () => {
      const values = form.getFieldsValue();
      await customerStore.update(values).then((result) => {
        if (!result) return;
        onClose();
        onSuccess?.();
      });
    }, [customerStore, form, onClose, onSuccess]);

    const handleAddressSearch = (value: string) => {
      form.setFieldsValue({ postcode: value });
    };

    return (
      <Drawer
        title={L('Edit Customer')}
        open={open}
        onClose={onClose}
        styles={{ wrapper: { width: 720 } }}
        destroyOnHidden
        extra={<DrawerCloseButton onClick={onClose} />}
        closable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={onClose}>{L('Cancel')}</Button>
              <Button type="primary" loading={customerStore.loading} onClick={handleFinish}>
                {L('Save changes')}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} initialValues={customer} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label={L('Name')} rules={Rules.required} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('Name')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="surname" label={L('Surname')} rules={Rules.required} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('Surname')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label={L('Email')} rules={Rules.email as any} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input disabled={!!customer?.userId} placeholder={L('Email')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label={L('Mobile')} rules={Rules.phone} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('Mobile')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="landline" label={L('Landline')} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('Landline')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="businessName" label={L('Business')} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('Business name')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="postcode" label={L('Postcode')} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <PostcodeSelect value={form.getFieldValue('postcode')} onSelect={handleAddressSearch} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="city" label={L('City')} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('City')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="country" label={L('Country')} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Input placeholder={L('Country')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label={L('Address')} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
            <Input placeholder={L('Address line 1')} />
          </Form.Item>
          <Form.Item name="secondAddress" label={L('Address 2')} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
            <Input placeholder={L('Address line 2')} />
          </Form.Item>
          <Form.Item name="thirdAddress" label={L('Address 3')} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
            <Input placeholder={L('Address line 3')} />
          </Form.Item>
        </Form>
      </Drawer>
    );
  }
);
